import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect } from 'react';
import {
    Table, Button, Space, Modal, Form, Input, Select, message,
    Card, Row, Col, Tag, Switch, Tooltip, Popconfirm, Empty
} from 'antd';
import {
    PlusOutlined, ReloadOutlined, PlayCircleOutlined, PauseCircleOutlined,
    DeleteOutlined, EditOutlined, ClockCircleOutlined, CaretRightOutlined
} from '@ant-design/icons';
import hostService from '../../../shared/services/hostService';

const { Option } = Select;
const { TextArea } = Input;

const CronManager = ({ containerId: propContainerId, embedded = false }) => {
    const { t } = useTranslation();
    const [containers, setContainers] = useState([]);
    const [selectedContainer, setSelectedContainer] = useState(propContainerId || null);
    const [cronJobs, setCronJobs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [editingJob, setEditingJob] = useState(null);
    const [form] = Form.useForm();

    // 预设的Cron表达式
    const cronPresets = [
        { label: t('Every minute'), value: '* * * * *' },
        { label: t('Every 5 minutes'), value: '*/5 * * * *' },
        { label: t('Every 10 minutes'), value: '*/10 * * * *' },
        { label: t('Every 30 minutes'), value: '*/30 * * * *' },
        { label: t('Hourly'), value: '0 * * * *' },
        { label: t('Every day at midnight'), value: '0 0 * * *' },
        { label: t('Every day at noon'), value: '0 12 * * *' },
        { label: t('Every Monday at midnight'), value: '0 0 * * 1' },
        { label: t('At midnight on the 1st of each month'), value: '0 0 1 * *' },
    ];

    useEffect(() => {
        if (embedded && propContainerId) {
            setSelectedContainer(propContainerId);
            return;
        }
        loadContainers();
        const urlParams = new URLSearchParams(window.location.search);
        const containerIdParam = urlParams.get('container_id');
        if (containerIdParam) {
            setSelectedContainer(parseInt(containerIdParam));
        }
    }, [propContainerId, embedded]);

    useEffect(() => {
        if (selectedContainer) {
            loadCronJobs();
        }
    }, [selectedContainer]);

    const loadContainers = async () => {
        try {
            const response = await hostService.getContainers();
            if (response.success) {
                setContainers(response.data || []);
            }
        } catch (error) {
            message.error(t('Failed to retrieve containers'));
        }
    };

    const loadCronJobs = async () => {
        if (!selectedContainer) return;
        setLoading(true);
        try {
            const response = await hostService.getCronJobs(selectedContainer);
            if (response.success) {
                setCronJobs(response.data || []);
            } else {
                message.error(response.error || t('Failed to retrieve scheduled tasks'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve scheduled tasks'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingJob(null);
        form.resetFields();
        form.setFieldsValue({
            user: 'hostmanager',
            schedule: '*/5 * * * *'
        });
        setModalVisible(true);
    };

    const handleEdit = (job) => {
        setEditingJob(job);
        form.setFieldsValue({
            name: job.name,
            schedule: job.schedule,
            command: job.command,
            user: job.user || 'hostmanager',
            description: job.description
        });
        setModalVisible(true);
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const data = {
                container_id: selectedContainer,
                ...values
            };

            let response;
            if (editingJob) {
                data.job_id = editingJob.id;
                response = await hostService.updateCronJob(data);
            } else {
                response = await hostService.createCronJob(data);
            }

            if (response.success) {
                message.success(editingJob ? t('Updated successfully') : t('Created successfully'));
                setModalVisible(false);
                loadCronJobs();
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (job) => {
        try {
            const response = await hostService.deleteCronJob(selectedContainer, job.id);
            if (response.success) {
                message.success(t('Deleted successfully'));
                loadCronJobs();
            } else {
                message.error(response.error || t('Deletion failed'));
            }
        } catch (error) {
            message.error(t('Deletion failed'));
        }
    };

    const handleToggleEnable = async (job) => {
        try {
            const response = job.enabled
                ? await hostService.disableCronJob(selectedContainer, job.id)
                : await hostService.enableCronJob(selectedContainer, job.id);

            if (response.success) {
                message.success(job.enabled ? t('Disabled') : t('Enabled'));
                loadCronJobs();
            } else {
                message.error(response.error || t('Operation failed'));
            }
        } catch (error) {
            message.error(t('Operation failed'));
        }
    };

    const handleRun = async (job) => {
        try {
            const response = await hostService.runCronJob(selectedContainer, job.id);
            if (response.success) {
                message.success(t('Task started'));
            } else {
                message.error(response.error || t('Execution failed'));
            }
        } catch (error) {
            message.error(t('Execution failed'));
        }
    };

    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            width: 150,
        },
        {
            title: t('Schedule'),
            dataIndex: 'schedule',
            key: 'schedule',
            width: 150,
            render: (schedule) => (
                <Tooltip title={t('Cron expression')}>
                    <Tag icon={<ClockCircleOutlined />}>{schedule}</Tag>
                </Tooltip>
            ),
        },
        {
            title: t('Command'),
            dataIndex: 'command',
            key: 'command',
            ellipsis: true,
            render: (command) => (
                <code style={{ fontSize: 12 }}>{command}</code>
            ),
        },
        {
            title: t('Run as user'),
            dataIndex: 'user',
            key: 'user',
            width: 100,
        },
        {
            title: t('Status'),
            dataIndex: 'enabled',
            key: 'enabled',
            width: 80,
            render: (enabled, record) => (
                <Switch
                    checked={enabled}
                    onChange={() => handleToggleEnable(record)}
                    checkedChildren={t('Enable')}
                    unCheckedChildren={t('Disable')}
                />
            ),
        },
        {
            title: t('Actions'),
            key: 'action',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Tooltip title={t('Run now')}>
                        <Button
                            type="link"
                            size="small"
                            icon={<CaretRightOutlined />}
                            onClick={() => handleRun(record)}
                        />
                    </Tooltip>
                    <Tooltip title={t('Edit')}>
                        <Button
                            type="link"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title={t('Delete this scheduled task?')}
                        onConfirm={() => handleDelete(record)}
                        okText={t('Confirm')}
                        cancelText={t('Cancel')}
                    >
                        <Tooltip title={t('Delete')}>
                            <Button
                                type="link"
                                size="small"
                                danger
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            {!embedded && <h2>{t('Scheduled tasks')}</h2>}
            <Card>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                    {!embedded && (
                        <Col span={8}>
                            <Select
                                style={{ width: '100%' }}
                                placeholder={t('Select a container')}
                                value={selectedContainer}
                                onChange={setSelectedContainer}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {containers.map(c => (
                                    <Option key={c.id} value={c.id}>
                                        {c.name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                    )}
                    <Col span={embedded ? 24 : 16}>
                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleCreate}
                                disabled={!selectedContainer}
                            >
                                {t('Create task')}
                            </Button>
                            <Button
                                icon={<ReloadOutlined />}
                                onClick={loadCronJobs}
                                disabled={!selectedContainer}
                            >
                                {t('Refresh')}
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={cronJobs}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? ' ' : (
                            <Empty description={selectedContainer ? t('No scheduled tasks yet. Click "Create task" to add one.') : t('Please select a container first')} />
                        ),
                    }}
                    pagination={false}
                />
            </Card>

            {/* 创建/编辑Modal */}
            <Modal
                title={editingJob ? t('Edit scheduled task') : t('Create scheduled task')}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={submitting}
                okText={editingJob ? t('Save') : t('Create')}
                width={600}
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item
                        name="name"
                        label={t('Task name')}
                        rules={[{ required: true, message: t('Please enter a task name') }]}
                    >
                        <Input placeholder={t('e.g. backup_database')} />
                    </Form.Item>

                    <Form.Item
                        name="schedule"
                        label={t('Schedule (Cron expression)')}
                        rules={[{ required: true, message: t('Please enter a schedule') }]}
                        extra={t('Format: minute hour day month weekday')}
                    >
                        <Input
                            placeholder="*/5 * * * *"
                            addonAfter={
                                <Select
                                    style={{ width: 120 }}
                                    placeholder={t('Quick select')}
                                    onChange={(value) => form.setFieldsValue({ schedule: value })}
                                >
                                    {cronPresets.map(preset => (
                                        <Option key={preset.value} value={preset.value}>
                                            {preset.label}
                                        </Option>
                                    ))}
                                </Select>
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        name="command"
                        label={t('Command')}
                        rules={[{ required: true, message: t('Please enter a command to run') }]}
                    >
                        <TextArea
                            rows={3}
                            placeholder={t('e.g. /usr/bin/php /var/www/html/artisan schedule:run')}
                        />
                    </Form.Item>

                    <Form.Item
                        name="user"
                        label={t('Run as user')}
                        initialValue="hostmanager"
                    >
                        <Select>
                            <Option value="hostmanager">hostmanager</Option>
                            <Option value="www">www</Option>
                            <Option value="root">root</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('Description')}
                    >
                        <TextArea rows={2} placeholder={t('Task description (optional)')} />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CronManager;
